import { Component, Input, OnInit, AfterViewInit, AfterViewChecked } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ProfileConfirmCloseModalComponent } from '../../modals/profile-confirm-close-modal/profile-confirm-close-modal.component';

import { UserService } from '../../services/user.service';
import { ToastrService } from 'ngx-toastr';

import * as Classes from '../../classes/models.classes';

@Component({
    selector: 'app-profile-edit-modal',
    templateUrl: './profile-edit-modal.component.html',
    styleUrls: ['./profile-edit-modal.component.css']
})
export class ProfileEditModalComponent implements OnInit {
    @Input() UserID: number;
    @Input() profileUserId: number;
    @Input() ProfileName: string;
    @Input() Username: string;
    @Input() UserImg: string;
    @Input() BannerImage: string;
    @Input() ImageDirectory: string;

    profileForm!: FormGroup;

    formData: Classes.ProfileModel = new Classes.ProfileModel();
    UserDataContent: Classes.ProfileModel = new Classes.ProfileModel();

    activeField: string | null = null;

    bannerPreviewUrl: string; // Default banner URL
    defaultProfileImage: string; // Default profile image URL

    currentUploadType: 'banner' | 'profile' | null = null; // Track the type of image being uploaded
    selectedBannerImage: File | null = null; // Store the selected banner image file
    selectedProfileImage: File | null = null; // Store the selected profile image file
    BannerImageDisplayUrl: string = ''; // URL for banner image preview
    UserImageDisplayUrl: string = ''; // URL for profile image preview

    constructor(
        private fb: FormBuilder,

        public activeModal: NgbActiveModal,
        private userService: UserService,
        private modalService: NgbModal,

        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        this.defaultProfileImage = "/img_profile/" + this.UserID + "/" + this.UserImg; // Default profile image URL
        this.BannerImageDisplayUrl = "/img_profile/" + this.UserID + "/" + this.BannerImage; // Default profile image URL

        // Initialize the Reactive Form
        this.profileForm = this.fb.group({
            ProfileName: ['', [Validators.required, Validators.maxLength(50)]],
            ProfileBio: ['', [Validators.maxLength(150)]],
            Location: ['', [Validators.maxLength(30)]],
            URL: [
                '',
                [
                    Validators.maxLength(100),
                    Validators.pattern(
                        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w.-]*)*\/?$/
                    ),
                ],
            ],
            BirthDate: [''], // No validation for date
        });

        // Load profile data
        this.loadProfileData();

    }

    private loadProfileData(): void {
        this.userService.getUserProfile(this.Username).subscribe({
            next: (profileData) => {
                // Patch form values with API response
                this.profileForm.patchValue({
                    ProfileName: profileData.ProfileName || '',
                    ProfileBio: profileData.ProfileBio || '',
                    Location: profileData.Location || '',
                    URL: profileData.URL || '',
                    BirthDate: profileData.BirthDate
                        ? new Date(profileData.BirthDate).toISOString().split('T')[0]
                        : '', // Format date for date input
                });

                // Update image display
                this.UserImageDisplayUrl = `/img_profile/${profileData.UserID}/${profileData.UserImg}`;
                this.BannerImageDisplayUrl = `/img_profile/${profileData.UserID}/${profileData.UserBannerImg}`;
            },
            error: (err) => {
                console.error('Error loading profile:', err);
                this.toastr.error('Failed to load profile data.', 'Error');
            },
        });
    }

    setActive(field: string): void {
        this.activeField = field;
    }

    removeActive(field: string): void {
        if (!this.profileForm.get(field)?.value) {
            this.activeField = ''; // Clear active field if no value is present
        }
    }

    formatDate(field: string, value: string): void {
        this.formData[field] = value ? new Date(value).toISOString().split('T')[0] : null;
    }

    selectBannerImage(event: Event): void {
        // Open file picker
        console.log('Select banner image clicked');
    }

    selectProfileImage(event: Event): void {
        // Open file picker
        console.log('Select profile image clicked');
    }

    triggerFileUpload(type: 'banner' | 'profile'): void {
        this.currentUploadType = type; // Save the type of upload
        document.getElementById('fileUpload')!.click(); // Trigger file upload
    }

    checkFileUpload(event: any): void {
        const file = event.target.files[0];

        if (!file) {
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Unsupported file type. Please upload an image file (JPEG, PNG, GIF).');
            return;
        }

        // Validate file size (e.g., 5MB limit)
        const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSizeInBytes) {
            alert('File size exceeds the 5MB limit.');
            return;
        }

        // Display the image preview
        const reader = new FileReader();
        reader.onload = (e: any) => {
            if (this.currentUploadType === 'banner') {
                this.BannerImageDisplayUrl = e.target.result; // Update banner preview
                this.selectedBannerImage = file; // Save the file for submission
            } else if (this.currentUploadType === 'profile') {
                this.UserImageDisplayUrl = e.target.result; // Update profile preview
                this.selectedProfileImage = file; // Save the file for submission
            }
        };
        reader.readAsDataURL(file);

        // Clear the input so the same file can be re-uploaded if necessary
        event.target.value = '';
    }


    saveProfile(): void {
        if (!this.profileForm.valid) {
            if (this.profileForm.get('URL')?.hasError('pattern')) {
                this.toastr.warning('Account Update failed: URL is invalid', 'Warning');
            }
            return;
        }
        if (this.profileForm.valid) {
            const updatedProfile = this.profileForm.value; // Get updated form values
            console.log('Saving profile:', updatedProfile);

            this.UserDataContent.UserID = this.UserID;
            this.UserDataContent.ProfileName = updatedProfile.ProfileName;
            this.UserDataContent.ProfileBio = updatedProfile.ProfileBio;
            this.UserDataContent.Location = updatedProfile.Location;
            this.UserDataContent.URL = updatedProfile.URL;
            this.UserDataContent.BirthDate = updatedProfile.BirthDate;
            this.UserDataContent.UserImg = this.UserImg;
            this.UserDataContent.UserBannerImg = this.BannerImage;

            const formData = new FormData();

            formData.append('postData', JSON.stringify(this.UserDataContent)); // Serialize your object into a JSON string

            // Append form data fields
            //formData.append('ProfileName', updatedProfile.ProfileName);
            //formData.append('ProfileBio', updatedProfile.ProfileBio);
            //formData.append('Location', updatedProfile.Location);
            //formData.append('URL', updatedProfile.URL);
            //formData.append('BirthDate', updatedProfile.BirthDate || '');

            // Append files if selected
            if (this.selectedBannerImage) {
                formData.append('bannerImage', this.selectedBannerImage, this.selectedBannerImage.name);
            }
            if (this.selectedProfileImage) {
                formData.append('profileImage', this.selectedProfileImage, this.selectedProfileImage.name);
            }

            this.userService.updateUserProfile(formData).subscribe({
                next: (response) => {
                    this.UserImageDisplayUrl = "/img_profile/" + this.UserID + "/" + this.formData.UserImg;
                    this.BannerImageDisplayUrl = "/img_profile/" + this.UserID + "/" + this.formData.UserBannerImg;

                    const updatedImages = {
                        UserImg: response.UserImg,
                        BannerImage: response.UserBannerImg,
                    };

                    this.toastr.success('Profile updated successfully!', 'Success');
                    this.activeModal.close(updatedImages); // Pass updated image names to the calling component
                },
                error: (err) => {
                    console.error('Error saving profile:', err);
                    this.toastr.error('Failed to save profile.', 'Error');
                },
            });
        } else {
            this.toastr.warning('Please fill out all required fields.', 'Warning');
        }
    }

    closeModal(): void {
        if (this.profileForm.dirty) {
            const modalRef = this.modalService.open(ProfileConfirmCloseModalComponent, {
                backdrop: 'static',
                centered: true,
            });

            modalRef.result
                .then((confirmed) => {
                    if (confirmed) {
                        this.activeModal.close(); // Close the main edit modal
                    }
                })
                .catch(() => {
                    // User canceled
                });
        } else {
            this.activeModal.close(); // Close directly if no changes
        }
    }


}
